/* eslint-disable prettier/prettier */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Theme, useMediaQuery } from '@mui/material';
import classnames from 'classnames';
import { AccessTimeRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useApiRequests, useDialog, useSubscription } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store';
import { getUser, selectUser } from 'store/user';
import { getIsAdminByRoles } from 'utils';
import { ApiUser, SubscriptionStatusEnum } from 'types';
import { SubscriptionDetailsPopup } from 'pages/SettingPage/components/SubscriptionSettings/components';
import { ConfirmResultEnum } from 'context';
import { Subscriptions } from 'constants/payment';

import styles from './SubscriptionStatusBanner.module.scss';

type SubscriptionStatusBannerProps = {
  variant?: 'banner' | 'card';
};

export function SubscriptionStatusBanner({ variant = 'banner' }: SubscriptionStatusBannerProps) {
  const { t } = useTranslation('providerRoom');
  const dialog = useDialog();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const apiRequests = useApiRequests();
  const dispatch = useAppDispatch();
  const subscription = useSubscription();
  const user: ApiUser | null = useAppSelector(selectUser);

  const isAdmin = useMemo<boolean>(() => {
    return getIsAdminByRoles(user?.clinicUser.roles || []);
  }, [user]);

  const isTrial = useMemo<boolean>(() => {
    return subscription?.subscriptionStatus === SubscriptionStatusEnum.Trial;
  }, [subscription]);

  const isExpiredOrCancelled = useMemo<boolean>(() => {
    return (
      subscription?.subscriptionStatus === SubscriptionStatusEnum.Expired ||
      subscription?.subscriptionStatus === SubscriptionStatusEnum.Cancelled
    );
  }, [subscription]);

  const isPaymentFailed = useMemo<boolean>(() => {
    return (
      subscription?.subscriptionStatus === SubscriptionStatusEnum.Dunning ||
      subscription?.subscriptionStatus === SubscriptionStatusEnum.Unpaid
    );
  }, [subscription]);

  const isNonRenewing = useMemo<boolean>(() => {
    return subscription?.subscriptionStatus === SubscriptionStatusEnum.NonRenewing;
  }, [subscription]);

  const isRedAlert = isExpiredOrCancelled || isPaymentFailed;

  const getRedAlertMessage = (): string => {
    if (isExpiredOrCancelled) {
      return t('subscriptionNoLongerActive');
    }

    if (isPaymentFailed) {
      return t('paymentFailed');
    }

    return t('subscriptionRequired');
  };

  const handleUpgradePlan = async () => {
    if (isMobile) {
      navigate('/subscriptionDetails');
      return;
    }

    const confirmationResult = await dialog.confirm({ content: SubscriptionDetailsPopup });
    if (confirmationResult === ConfirmResultEnum.Confirm) {
      dispatch(getUser()).unwrap();
    }
  };

  const handleKeepMyPlan = async () => {
    const response = await apiRequests.reactivateCurrentSubscription();
    if (response.code === Subscriptions.Responses.StatusCodes.Success) {
      dispatch(getUser()).unwrap();
    }
  };

  const getDaysSeverityClass = (days?: number): string => {
    if ((days ?? 0) <= 2) {
      return styles.daysCritical;
    }

    if ((days ?? 0) <= 5) {
      return styles.daysWarning;
    }

    return styles.daysHealthy;
  };

  const getBannerSeverityClass = (days?: number): string => {
    if ((days ?? 0) <= 2) {
      return styles.bannerCritical;
    }

    if ((days ?? 0) <= 5) {
      return styles.bannerWarning;
    }

    return styles.bannerHealthy;
  };

  if (!subscription?.isSubscriptionInfoReady) {
    return null;
  }

  if (!(variant === 'card' || isAdmin) || (!isRedAlert && !isNonRenewing && !isTrial)) {
    return null;
  }

  if (variant === 'card') {
    const isCardAlert = isRedAlert || (isTrial && subscription?.isLessThanThreeDaysLeft);

    return (
      <Box component='div' className={classnames(styles.cardContainer, { [styles.cardAlert]: isCardAlert })}>
        <Box component='div' className={styles.cardTitle}>
          {isTrial && <AccessTimeRounded className={styles.cardTitleIcon} />}
          <span>{isTrial ? t('freeTrial') : isNonRenewing ? t('subscriptionEnding') : t('subscriptionRequired')}</span>
        </Box>

        {isTrial && (
          <Box component='div' className={styles.cardText}>
            <span className={getDaysSeverityClass(subscription?.trialDaysLeft)}>
              {subscription?.trialDaysLeft} {t('days')}
            </span>{' '}
            {t('remaining')}
          </Box>
        )}

        {isNonRenewing && (
          <Box component='div' className={styles.cardText}>
            <span className={getDaysSeverityClass(subscription?.daysUntilBillingPeriodEnd)}>
              {subscription?.daysUntilBillingPeriodEnd} {t('days')}
            </span>{' '}
            {t('remaining')}
          </Box>
        )}

        {isRedAlert && <Box className={styles.cardText}>{getRedAlertMessage()}</Box>}

        <Button LinkComponent={Link} onClick={isNonRenewing ? handleKeepMyPlan : handleUpgradePlan}>
          {isNonRenewing ? t('keepYourPlan') : t('upgradePlan')}
        </Button>
      </Box>
    );
  }

  return (
    <>
      {isRedAlert && (
        <Box component='div' className={classnames(styles.container, styles.alert)}>
          <Box component='div' className={styles.contentWrapper}>
            <Box component='div' className={styles.textWrapper}>
              {getRedAlertMessage()}
            </Box>
            <Box component='div' className={styles.buttonWrapper}>
              <Button LinkComponent={Link} className={styles.bannerButton} onClick={handleUpgradePlan}>
                {t('upgradeNow')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {isNonRenewing && (
        <Box
          component='div'
          className={classnames(styles.container, getBannerSeverityClass(subscription?.daysUntilBillingPeriodEnd))}
        >
          <Box component='div' className={styles.contentWrapper}>
            <Box component='div' className={styles.textWrapper}>
              {t('youHave')}&nbsp;
              <span>{t('daysRemainingPeriod', { days: subscription?.daysUntilBillingPeriodEnd })}</span>
              &nbsp;&nbsp;
            </Box>
            <Box component='div' className={styles.buttonWrapper}>
              <Button LinkComponent={Link} className={styles.bannerButton} onClick={handleKeepMyPlan}>
                {t('keepYourPlan')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {isTrial && (
        <Box
          component='div'
          className={classnames(styles.container, getBannerSeverityClass(subscription?.trialDaysLeft))}
        >
          <Box component='div' className={styles.contentWrapper}>
            <Box component='div' className={styles.textWrapper}>
              {t('youHave')}&nbsp;
              <span>{t('daysRemainingPeriod', { days: subscription?.trialDaysLeft })}</span>
              &nbsp;&nbsp;
            </Box>
            <Button LinkComponent={Link} className={styles.bannerButton} onClick={handleUpgradePlan}>
              {t('upgradeNow')}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}
